'use client';

import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface SelectOption {
  value: string;
  label: string;
  /**
   * How the label is drawn, where a word is not enough: a state's own chip,
   * so an open list and the column behind it speak one language. `label` is
   * still the string this option is searched and sorted by.
   */
  labelNode?: React.ReactNode;
  /** A line under the label, what choosing this actually means. */
  hint?: string;
  /** A short value — a count, a code — set flush right on the label's own
   *  line, the way a currency picker sets "EUR" beside "Euro". For anything
   *  longer than a word or two, use `hint` instead: this one line has no
   *  room to wrap. */
  count?: React.ReactNode;
  disabled?: boolean;
  /**
   * A small mark before the label — a platform's initial in its own colour,
   * in practice — so a list of names that otherwise look alike (OnlyFans,
   * Fansly, Chaturbate) can be told apart before the eye reaches the word.
   * Carried into the closed trigger too, for the one option that is chosen.
   */
  icon?: React.ReactNode;
  /** A heading this option sits under. Only labels the runs the caller has
   *  already ordered — a group whose members are scattered gets its heading
   *  twice rather than this quietly reordering somebody's sort. */
  group?: string;
}

/**
 * The one dropdown.
 *
 * A native `<select>` cannot be dressed: the list it opens is drawn by the
 * operating system, in its own font, with its own blue, and nothing can be put
 * inside it, no search, no second line explaining what a role may do. Three
 * places in the panel had one and each looked like a different product's.
 *
 * So the list is ours. What the native control gets right is kept: it is one
 * tab stop, arrows move through the options, Enter takes one, Escape leaves
 * without changing anything, and typing jumps to a match. What it gets wrong
 * for us: the appearance, and the absence of a way to find an option among
 * forty, is replaced.
 *
 * The panel is rendered into `document.body`. Every list of ours lives inside
 * something that scrolls or clips: a table with `overflow-x`, a dialog, a card.
 * Positioned in place it was cut off by whichever ancestor happened to clip
 * first, which is not a thing to discover per call site.
 */
interface SelectCommon {
  options: readonly SelectOption[];
  id?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  placeholder?: string;
  /** Defaults to on once a list is longer than a glance, see SEARCH_FROM. */
  searchable?: boolean;
  searchPlaceholder?: string;
  title?: string;
  'aria-label'?: string;
  /** A class on the control's own wrapper, for the call site that has to make
   *  the trigger part of something bigger — half of a split button, say. The
   *  panel is unaffected: it is portalled and belongs to everybody. */
  className?: string;
  /** A class on the portalled panel, for the one place the list opens over a
   *  ground the app's own surfaces do not belong on: the dark rail. */
  popClass?: string;
  /** Something other than the chevron on the closed control, where the list is
   *  a means to an act with a name — handing a client to somebody else — and
   *  an arrow only says "a list opens". Single-choice only. */
  triggerIcon?: React.ReactNode;
  /**
   * What the trigger says once more than one thing is chosen: the plural of
   * whatever this picks, so it reads "2 statuses" rather than "2 selected".
   * Multiple-choice only.
   */
  manyLabel?: string;
  /**
   * Turns the footer under a several-choice list from "let this set go" into
   * "put everything back", for a list where an empty set means nothing: the
   * column picker shows every lane when none is ticked, so `Clear all 8` was a
   * row that did nothing. Shown only while something is actually missing.
   */
  resetLabel?: string;
  /**
   * Several-choice only: show what is chosen as removable chips inside the
   * control instead of counting them.
   *
   * Counting is right where the control sits in a narrow column and the set is
   * read as one filter ("2 statuses"). Chips are right where the set *is* the
   * question — a filter bar somebody builds up and takes apart — because
   * removing one thing then costs one click on that thing rather than opening
   * the list and hunting for the ticked row.
   */
  tags?: boolean;
  /**
   * Allow a value that is not in the list, typed into the search box.
   *
   * For filters over data the list cannot know all of: a domain nobody has
   * opened a case for yet, a name spelled the way the client spells it. The
   * typed value is passed through as it stands, so the caller decides what it
   * means.
   */
  freeText?: boolean;
}

/**
 * One choice or several, in one control, because the difference between them
 * is what a click does to the list and nothing else: the panel, its keyboard,
 * its search and its placing are the same thing either way, and were not going
 * to survive being written twice.
 *
 * The two shapes are exclusive on purpose. A `value` that might be a string or
 * an array reaches every call site as a thing to narrow before use; declaring
 * `multiple` picks the pair that goes with it, so a single-choice caller
 * cannot be handed an array by accident.
 */
type SelectProps = SelectCommon &
  (
    | { multiple?: false; value: string; onChange: (value: string) => void }
    | { multiple: true; value: readonly string[]; onChange: (value: string[]) => void }
  );

export function Select(props: SelectProps) {
  const {
    value,
    options,
    id,
    disabled = false,
    size = 'md',
    placeholder = 'Choose…',
    searchable,
    searchPlaceholder = 'Type to filter',
    title,
    'aria-label': ariaLabel,
    manyLabel,
  resetLabel,
    tags,
    freeText,
    className,
  popClass,
    triggerIcon,
  } = props;
  const multiple = props.multiple === true;
  const generatedId = useId();
  const listId = `${id ?? generatedId}-list`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [place, setPlace] = useState<{ top: number; left: number; width: number; above: boolean } | null>(null);
  /* The coordinates the panel is actually drawn at. State only remembers which
     side it opened on; writing top and left through a render leaves them a
     frame behind the trigger while the page moves. */
  const placeRef = useRef<{ top: number; left: number; width: number; above: boolean } | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  /**
   * `freeText` forces the box on however short the list is.
   *
   * The search box otherwise appears only once a list is long enough to hunt
   * through — and that box is the only place a value can be typed, so a
   * six-row list with free entry offered no way to enter anything.
   */
  const withSearch = searchable ?? (freeText === true || options.length >= SEARCH_FROM);
  /** One list either way, so every "is this one chosen" test reads the same. */
  const chosen = useMemo(
    () => (multiple ? (value as readonly string[]).filter(Boolean) : value ? [value as string] : []),
    [multiple, value],
  );

  /** A one-choice list with an answer already in it: the tick column is drawn
   *  on every row so the rows keep one shape. */
  const marksChoice = !multiple && chosen.some(Boolean);
  const selected = options.find((option) => option.value === chosen[0]) ?? null;
  const asTags = multiple && tags === true;
  /** A chosen value's own words: the option's label, or the value itself when
   *  it was typed rather than picked. */
  const labelFor = useCallback(
    (raw: string) => options.find((option) => option.value === raw)?.label ?? raw,
    [options],
  );
  /**
   * What the closed control says. One choice names itself; several are counted,
   * because a trigger that lists them either grows past its column or truncates
   * the one you were checking for.
   */
  /* Nothing for the footer to do: no set to let go of, or — where it puts
     things back rather than clearing them — nothing missing to put back. */
  const footerIdle = resetLabel ? chosen.length >= options.length : chosen.length === 0;

  /* It is not drawn at all while there is nothing to do, so the panel does not
     end in a band of empty white. What that costs is the guard the reserved
     space used to give: the row arrives under a pointer that was clicking the
     last option. So it ignores its own first moments rather than its place. */
  const footerSince = useRef(0);
  useEffect(() => {
    if (!footerIdle) footerSince.current = Date.now();
  }, [footerIdle]);

  const triggerLabel =
    chosen.length > 1
      ? // Everything ticked is a state of its own, not a number: "All columns"
        // says the table is whole, where "14 columns" makes the reader count
        // the headings to find out whether anything is missing.
        chosen.length === options.length
        ? `All ${manyLabel ?? 'selected'}`
        : `${chosen.length} ${manyLabel ?? 'selected'}`
      : (selected?.label ?? placeholder);

  /* Room for the longest thing this control can ever say, held from the first
     paint. Without it the trigger was the width of whatever was chosen, so
     picking "Paying" after "Any status" shrank the control under the pointer
     and moved everything in the toolbar beside it. Capped, because one long
     option must not set the width of a filter row. */
  const triggerRoom = useMemo(() => {
    const words = [
      placeholder,
      ...options.map((option) => (typeof option.label === 'string' ? option.label : '')),
      multiple ? `${options.length} ${manyLabel ?? 'selected'}` : '',
      multiple ? `All ${manyLabel ?? 'selected'}` : '',
    ];
    const longest = words.reduce((most, word) => Math.max(most, word.length), 0);
    return Math.min(24, longest + 1);
  }, [manyLabel, multiple, options, placeholder]);

  /*
    The "all of them" row is not a row in the several-choice form.

    It used to sit first in the list, one pixel below the pointer that had just
    opened it — so a second click where the first one landed, or an Enter while
    the keyboard was still on row zero, wiped every chip the reader had
    collected (Artur, 2026-09-18). Clearing is still there, as a footer under
    the list and only while there is something to clear: a destructive act does
    not share an edge with the pointer that opened the panel.
  */
  const pickable = useMemo(
    () => (multiple ? options.filter((option) => option.value) : options),
    [multiple, options],
  );

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return pickable;

    // Names first, and only names if any of them match. Searching the hints at
    // the same time meant typing the name of one option kept another on screen
    // because the word appeared in its description, "support" matched Admin,
    // whose hint says what it can do beyond support. The hints are still
    // searchable, but only when nothing is called what you typed.
    const byLabel = pickable.filter((option) => option.label.toLowerCase().includes(needle));
    if (byLabel.length > 0) return byLabel;

    return pickable.filter((option) => (option.hint ?? '').toLowerCase().includes(needle));
  }, [pickable, query]);

  /* Above or below, remembered for the life of one opening. Null while closed
     — the next opening decides again, from where the trigger is then. */
  const side = useRef<boolean | null>(null);

  const position = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const pop = popRef.current?.offsetHeight ?? 0;
    const room = window.innerHeight - rect.bottom;
    // Open upwards when the space below cannot hold the list and the space
    // above can. A list that opens downwards into six visible pixels is worse
    // than one that opens the other way.
    /* Which side it opens on is decided once, when it opens, and kept while it
       is open: recomputed on every scroll event, the list flipped from under
       the trigger to over it and back as the page moved, which reads as the
       list sliding about rather than following. */
    const fits = pop > 0 && room < pop + 12 && rect.top > room;
    if (side.current === null && pop > 0) side.current = fits;
    const above = side.current ?? fits;
    /* Pulled back inside the window when the trigger sits near its right edge
       — an icon-wide picker at the end of a drawer row put its list half off
       the screen, and a list you cannot read is not a list. */
    const want = popRef.current?.offsetWidth ?? rect.width;
    /* A list that will not fit to the right of its trigger hangs off the
       trigger's right edge instead of the window's: an icon-wide button at the
       end of a row left it pinned to the screen edge, under the scrollbar.
       `clientWidth`, never `innerWidth` — the scrollbar is not room. */
    const edge = document.documentElement.clientWidth;
    const spills = rect.left + want > edge - 8;
    const wants = spills ? rect.right - want : rect.left;
    const left = Math.max(8, Math.min(wants, edge - want - 8));
    /* Document coordinates: the panel is position:absolute, so these stay
       put while the page scrolls and the browser moves the panel with the
       trigger. Viewport numbers would need rewriting on every scroll, and
       that rewrite lands the frame after the trigger has moved. */
    const next = {
      top: (above ? rect.top - pop - 6 : rect.bottom + 6) + window.scrollY,
      left: left + window.scrollX,
      // Never narrower than a label and its tick can sit apart in. Matched to
      // the trigger otherwise, but a trigger reading "2 types" is no measure
      // of the words the list has to hold, and the panel is in the body, so
      // overhanging its column costs nothing. Skipped for `sm`: that size is
      // short lists of short words (a page-size picker, a sort order), where
      // the floor only stretched the panel into blank space past the label.
      width: size === 'sm' ? rect.width : Math.max(rect.width, PANEL_FLOOR),
      above,
    };
    placeRef.current = next;
    /* Onto the node now. A setState is painted after this frame, so during a
       scroll the list stayed where it was opened and lay across the trigger
       until the scroll stopped. */
    if (popRef.current) {
      popRef.current.style.top = `${next.top}px`;
      popRef.current.style.left = `${next.left}px`;
      popRef.current.style.minWidth = `${next.width}px`;
      popRef.current.style.visibility = 'visible';
    }
    setPlace((current) => (current?.above === next.above ? current : next));
    // `size` read from the closed-over prop, not a dependency: it does not
    // change per instance, and this already reruns on every open via `position()`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      side.current = null;
      return;
    }
    position();
    /* Once more after the panel exists: the first pass has nothing to measure,
       so a list opened by a trigger near the window's right edge learned its
       own width only on the second. */
    const measured = requestAnimationFrame(position);
    // Capture phase, so a list inside a scrolling table follows its trigger
    // rather than hanging in the air where it was opened. The document's own
    // scroll is not one of these: the panel lives in the document and moves
    // with it, and rewriting its position from this event puts it a frame late.
    const onScroll = (event: Event) => {
      const target = event.target;
      if (target === document || target === document.documentElement || target === document.body) return;
      position();
    };
    const onResize = () => {
      side.current = null;
      position();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(measured);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, position, shown.length]);

  /* A later render paints `place`, which is the position from when the list
     opened. The scroll handler has since moved the node. Put that back before
     the browser paints, or the list jumps onto the trigger for a frame. */
  useLayoutEffect(() => {
    const drawn = placeRef.current;
    const pop = popRef.current;
    if (!open || !drawn || !pop) return;
    pop.style.top = `${drawn.top}px`;
    pop.style.left = `${drawn.left}px`;
    pop.style.minWidth = `${drawn.width}px`;
    pop.style.visibility = 'visible';
  });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  });

  useEffect(() => {
    if (!open) return;
    if (withSearch) searchRef.current?.focus();
    const at = shown.findIndex((option) => option.value === chosen[0]);
    setActiveIndex(at >= 0 ? at : 0);
    // `chosen[0]` and not `chosen`: the arrow starts where the reader's first
    // choice is, and re-running this on every tick would drag it back there
    // mid-list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, withSearch, options, chosen[0]]);

  // Keep the active option in view when the arrows walk past the panel's edge.
  //
  // Scrolled by hand inside the list rather than with `scrollIntoView`: that
  // walks every scrollable ancestor up to the window, so opening the page-size
  // menu at the foot of a table jumped the whole page to it. Only the list
  // moves here, and only when the row is actually outside it.
  useEffect(() => {
    if (!open) return;
    const list = popRef.current?.querySelector<HTMLElement>('.select-list');
    const row = popRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    if (!list || !row) return;
    const box = list.getBoundingClientRect();
    const at = row.getBoundingClientRect();
    if (at.top < box.top) list.scrollTop -= box.top - at.top;
    else if (at.bottom > box.bottom) list.scrollTop += at.bottom - box.bottom;
  }, [open, activeIndex]);

  function close(returnFocus = true) {
    setOpen(false);
    setQuery('');
    // `preventScroll`: the trigger may sit under the fold once the list it
    // filtered has grown, and the browser's own "scroll to what you focused"
    // then yanks the page after a pick nobody asked to navigate.
    if (returnFocus) triggerRef.current?.focus({ preventScroll: true });
  }

  /**
   * Take one chip off the set. Never a `<button>` at the call site: these
   * filters sit inside a `<label>`, which forwards a click on itself to the
   * first labelable element in it — so clicking the field removed a chip.
   */
  function remove(raw: string) {
    (props.onChange as (next: string[]) => void)(chosen.filter((one) => one !== raw));
  }

  function choose(option: SelectOption) {
    if (option.disabled) return;

    // Narrowed off `props` rather than off the `multiple` local: a boolean
    // pulled out of a union does not carry which half of it came, so the two
    // `onChange` signatures stay unreachable behind it.
    if (props.multiple !== true) {
      props.onChange(option.value);
      close();
      return;
    }

    // The empty value is the "all of them" row: it clears rather than joining
    // the set, which is the only way back out of a filter once several things
    // are ticked.
    if (!option.value) {
      props.onChange([]);
      close();
      return;
    }

    // Left open. Ticking one of several is rarely the last thing the reader
    // came to do, and a list that closes on every tick has to be reopened
    // once per choice.
    props.onChange(
      chosen.includes(option.value)
        ? chosen.filter((each) => each !== option.value)
        : [...chosen, option.value],
    );
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        close(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((at) => step(shown, at, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((at) => step(shown, at, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(step(shown, -1, 1));
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(step(shown, shown.length, -1));
        break;
      case 'Enter':
        event.preventDefault();
        if (shown[activeIndex]) choose(shown[activeIndex]);
        break;
      default:
        // Type-ahead, for the lists that have no search box. With one, the
        // letters belong to it.
        if (!withSearch && event.key.length === 1 && /\S/.test(event.key)) {
          const from = shown.findIndex(
            (option, index) => index > activeIndex && option.label.toLowerCase().startsWith(event.key.toLowerCase()),
          );
          const wrapped =
            from >= 0
              ? from
              : shown.findIndex((option) => option.label.toLowerCase().startsWith(event.key.toLowerCase()));
          if (wrapped >= 0) setActiveIndex(wrapped);
        }
    }
  }

  /**
   * The open list, declared once and rendered by whichever trigger form is in
   * use. Chips and the plain button share every part of this: the search, the
   * keyboard, the placing, the portal out of whatever ancestor clips.
   */
  const panel =
    mounted && open
      ? createPortal(

          <div
            ref={popRef}
            className={`select-pop select-${size}${place?.above ? ' is-above' : ''}${popClass ? ` ${popClass}` : ''}`}
            style={{
              top: place?.top ?? -9999,
              left: place?.left ?? -9999,
              minWidth: place?.width ?? 0,
              visibility: place ? 'visible' : 'hidden',
            }}
            onKeyDown={onKeyDown}
          >
            {/* How many are ticked, said once at the top. With the chips in
                the control above and the ticks down the list, the number
                itself was the one thing a reader had to count by eye. */}
            {multiple && (
              /* Drawn at zero too. Appearing only once something was ticked
                 made the whole list jump down a line on the first pick and
                 back up on the last. */
              <p className="select-chosen-count">{`${chosen.length} chosen`}</p>
            )}
            {withSearch && (
              <div className="select-searchwrap">
                <SearchIcon />
                <input
                  ref={searchRef}
                  className="select-search"
                  value={query}
                  placeholder={freeText ? `${searchPlaceholder}, or type your own` : searchPlaceholder}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setActiveIndex(0);
                  }}
                  aria-controls={listId}
                  aria-label={searchPlaceholder}
                />
              </div>
            )}

            <ul
              className="select-list"
              id={listId}
              role="listbox"
              aria-multiselectable={multiple || undefined}
              aria-activedescendant={`${listId}-${activeIndex}`}
            >
              {shown.map((option, index) => {
                // The "all of them" row is ticked by nothing being ticked: it
                // is the state the filter is in, not a value beside the others.
                const isChosen = option.value
                  ? chosen.includes(option.value)
                  : chosen.length === 0;
                // A heading once per run, compared against the row above as it
                // is drawn: filtering by search removes rows, and a heading
                // decided when the list was built would survive its own group.
                const heading =
                  option.group && option.group !== shown[index - 1]?.group ? option.group : null;
                return (
                <Fragment key={option.value}>
                {heading && (
                  <li className="select-group" role="presentation">
                    {heading}
                  </li>
                )}
                <li
                  id={`${listId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={isChosen}
                  aria-disabled={option.disabled}
                  // The row that clears the set is not one of the values, and
                  // sitting flush against them it read as a third one.
                  data-clears={multiple && !option.value ? 'true' : undefined}
                  className={`select-option${index === activeIndex ? ' is-active' : ''}${
                    isChosen ? ' is-chosen' : ''
                  }${option.disabled ? ' is-disabled' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={(event) => {
                    // React bubbles a portalled click through the component
                    // tree, not the real DOM: without this, picking a row in
                    // the tags form also reached the tagbox's own onClick and
                    // closed the list — the same hazard the tag's own ✕
                    // already guards against, just missed here.
                    event.stopPropagation();
                    choose(option);
                  }}
                >
                  {/* A box, not only a tick: several-choice rows have to say
                      they are several-choice before one is picked, and an
                      empty right-hand slot says nothing at all. It sits beside
                      the whole row rather than inside its first line, so on a
                      two-line option it is centred against both lines. */}
                  {multiple && (
                    <span className={`select-option-box${isChosen ? ' is-on' : ''}`} aria-hidden>
                      {isChosen && <TickIcon />}
                    </span>
                  )}
                  <span className="select-option-text">
                    <span className="select-option-label">
                      <span className="select-option-name">
                        {option.icon && (
                          <span className="select-option-icon" aria-hidden>
                            {option.icon}
                          </span>
                        )}
                        <span>{option.labelNode ?? mark(option.label, query)}</span>
                      </span>
                      {option.count !== undefined && (
                        <span className="select-option-count">{option.count}</span>
                      )}
                    </span>
                    {option.hint && (
                      <span className="select-option-hint">{mark(option.hint, query)}</span>
                    )}
                  </span>
                  {/* One-choice lists say which row is the current answer the
                      only way they can: the weight change alone was read as
                      hover by everybody who met it. Drawn empty on the other
                      rows so one ticked row does not shorten every line
                      beside it. */}
                  {marksChoice && (
                    <span className="select-option-tick" aria-hidden>
                      {isChosen && option.value && <TickIcon />}
                    </span>
                  )}
                </li>
                </Fragment>
                );
              })}

              {/*
                Whatever was typed, as a value of its own.
                
                For filters over data no list can hold all of — a domain
                nobody has opened a case for yet, a name spelled the client's
                way. Shown only when it is not already an option or already
                chosen, so it never duplicates a row above it.
              */}
              {freeText &&
                query.trim().length > 0 &&
                !options.some((option) => option.label.toLowerCase() === query.trim().toLowerCase()) &&
                !chosen.includes(query.trim()) && (
                  <li
                    className="select-option select-option-add"
                    role="option"
                    aria-selected={false}
                    onClick={(event) => {
                      event.stopPropagation();
                      const typed = query.trim();
                      if (multiple) {
                        (props.onChange as (next: string[]) => void)([...chosen, typed]);
                        setQuery('');
                      } else {
                        (props.onChange as (next: string) => void)(typed);
                        close(true);
                      }
                    }}
                  >
                    <span className="select-option-label">
                      <span className="select-option-name">{`Use “${query.trim()}”`}</span>
                    </span>
                  </li>
                )}

              {shown.length === 0 && !freeText && (
                <li className="select-empty">Nothing matches “{query.trim()}”</li>
              )}
            </ul>

            {/* Under the list, not in it: see `pickable`. Drawn only when
                there is a set to let go of. */}
            {multiple && (
              /* Its place is held whether or not there is a set to let go of,
                 because a footer that appears under the pointer is how a click
                 meant for the last option lands on "clear everything". With
                 nothing chosen it keeps the space and says nothing: a disabled
                 row reading "Nothing to clear" is a control offering itself on
                 every list that has never been touched. */
              !footerIdle && (
                <button
                  type="button"
                  className="select-clear"
                  onClick={(event) => {
                    event.stopPropagation();
                    // The click that made this row appear must not also press
                    // it: a tick on the last option and this row are half a
                    // second and four pixels apart.
                    if (Date.now() - footerSince.current < 350) return;
                    (props.onChange as (next: string[]) => void)(
                      resetLabel ? options.map((option) => option.value) : [],
                    );
                    close(true);
                  }}
                >
                  {resetLabel ?? `Clear ${chosen.length === 1 ? 'this one' : `all ${chosen.length}`}`}
                </button>
              )
            )}
          </div>,
          document.body,
        )
      : null;

  /**
   * Chips live in a div, not the button.
   *
   * Each chip carries its own remove control, and a button inside a button is
   * invalid markup that browsers resolve by dropping one of them — in practice
   * the ✕ stopped being clickable. So the chip form is a focusable combobox
   * div with the same keyboard as the button form.
   */
  if (asTags) {
    return (
      <div className={`select select-${size} has-tags`}>
        <div
          id={id}
          ref={triggerRef as unknown as React.RefObject<HTMLDivElement>}
          className={`select-trigger select-tagbox${open ? ' is-open' : ''}`}
          style={{ '--trigger-ch': triggerRoom } as React.CSSProperties}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          title={title}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-disabled={disabled || undefined}
          onClick={() => (open ? close(false) : setOpen(true))}
          onKeyDown={onKeyDown}
        >
          {chosen.length === 0 ? (
            <span className="select-value is-empty">{placeholder}</span>
          ) : (
            /* Every choice as its own chip, on one line inside a control that
               keeps its width: they give way to each other rather than making
               the control grow and push the filters beside it along the row. */
            chosen.map((raw) => (
              <span key={raw} className="select-tag">
                {labelFor(raw)}
                <span
                  role="button"
                  tabIndex={-1}
                  className="select-tag-x"
                  aria-label={`Remove ${labelFor(raw)}`}
                  onClick={(event) => {
                    // The chip is inside the control that opens the list, so
                    // removing one must not also open it.
                    event.stopPropagation();
                    remove(raw);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    event.stopPropagation();
                    remove(raw);
                  }}
                >
                  {'✕'}
                </span>
              </span>
            ))
          )}
          {/* Out of the chips' flow entirely: pinned to the field's right
              edge, over a lane the chips are padded out of. The pointer goes
              straight through it to the field, which opens the list — so the
              arrow is a sign that cannot be missed *or* hit, and the ✕ it
              used to sit beside is never underneath it. */}
          <ChevronIcon />
        </div>
        {panel}
      </div>
    );
  }

  return (
    <div className={`select select-${size}${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className="select-trigger"
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={onKeyDown}
      >
        {/* Greyed only where nothing chosen means nothing said. A
            several-choice control with an empty set is in its "all of them"
            state, which is an answer, not a blank waiting to be filled. */}
        <span
          className={
            chosen.length > 0 || multiple ? 'select-value' : 'select-value is-empty'
          }
          style={{ '--trigger-ch': triggerRoom } as React.CSSProperties}
        >
          {selected?.icon && chosen.length <= 1 && (
            <span className="select-value-icon" aria-hidden>
              {selected.icon}
            </span>
          )}
          {triggerLabel}
        </span>
        {triggerIcon ? (
          <span className="select-trigger-icon" aria-hidden>
            {triggerIcon}
          </span>
        ) : (
          <ChevronIcon />
        )}
      </button>

      {panel}
    </div>
  );
}

/** Longer than this and the eye stops scanning and starts hunting. */
const SEARCH_FROM = 8;

/** Narrowest the open list gets, whatever its trigger measures. Only a floor:
 *  the width comes from the content (see `.select-pop`), because a trigger is
 *  sized by its own words and those say nothing about the names inside. */
const PANEL_FLOOR = 200;

/**
 * The typed text, shown where it landed.
 *
 * A filtered list without this asks the reader to work out why each survivor is
 * still there, which matters most in the case that survives on its hint rather
 * than its name, where the word is somewhere in a sentence.
 */
function mark(text: string, query: string): React.ReactNode {
  const needle = query.trim();
  if (!needle) return text;

  const parts: React.ReactNode[] = [];
  const haystack = text.toLowerCase();
  const lower = needle.toLowerCase();
  let at = 0;

  for (let found = haystack.indexOf(lower); found !== -1; found = haystack.indexOf(lower, at)) {
    if (found > at) parts.push(text.slice(at, found));
    parts.push(
      <mark key={found} className="select-mark">
        {text.slice(found, found + needle.length)}
      </mark>,
    );
    at = found + needle.length;
  }

  if (at === 0) return text;
  if (at < text.length) parts.push(text.slice(at));
  return parts;
}

/** Next enabled option in a direction, wrapping at the ends. */
function step(options: readonly SelectOption[], from: number, by: 1 | -1): number {
  if (options.length === 0) return 0;
  let at = from;
  for (let i = 0; i < options.length; i++) {
    at = (at + by + options.length) % options.length;
    if (!options[at]?.disabled) return at;
  }
  return from;
}

function ChevronIcon() {
  return (
    <svg className="select-chevron" width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M3.8 6L8 10.2L12.2 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
      <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10.5 10.5L14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function TickIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M3.5 8.5l3 3 6-6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
